import styled, { keyframes } from 'styled-components';

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 currentColor; }
  50% { box-shadow: 0 0 8px 3px currentColor; }
`;

// {@code flex-shrink: 0} keeps the run log sized to its full content
// rather than being squished by the surrounding {@code PanelBody} flex
// column. The user explicitly wants the entire step list visible — if
// it doesn't fit alongside the other cards the sidebar as a whole
// scrolls, not this card individually.
export const LogCard = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 24px;
  background: ${({ theme: { colors = {} } }) => colors.cardSurface ?? '#15151F'};
  border: 1px solid
    ${({ theme: { colors = {} } }) => colors.outlineVariant ?? '#25263A'};
  overflow: hidden;
  flex-shrink: 0;
`;

export const LogHead = styled.div`
  padding: 18px 18px 10px;
`;

export const LogTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
`;

export const LogStatusDot = styled.span<{
  $state: 'running' | 'done' | 'failed';
}>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  color: ${({ $state, theme: { colors = {} } }) => {
    if ($state === 'done') return colors.successFixed ?? '#4ADE80';
    if ($state === 'failed') return colors.errorFixed ?? '#FF6B6B';
    return colors.primary ?? '#C1C1FF';
  }};
  background: currentColor;
  animation: ${({ $state }) =>
      $state === 'running' ? pulse : 'none'}
    2s ease-in-out infinite;
`;

export const LogTitleText = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E6E6F2'};
`;

export const MetaGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const MetaRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
`;

export const MetaKey = styled.span`
  width: 90px;
  flex-shrink: 0;
  font-size: 12px;
  color: ${({ theme: { colors = {} } }) =>
    colors.onSurfaceVariant ?? '#A1A3B8'};
`;

export const MetaValue = styled.span`
  font-size: 12px;
  color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E6E6F2'};
`;

export const MetaMono = styled(MetaValue)`
  font-variant-numeric: tabular-nums;
`;

export const LogSep = styled.div`
  border-top: 1px solid
    ${({ theme: { colors = {} } }) => colors.outlineVariant ?? '#25263A'};
  padding: 8px 18px 2px;
  flex-shrink: 0;
`;

export const LogSepLabel = styled.span`
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme: { colors = {} } }) =>
    colors.onSurfaceVariant ?? '#A1A3B8'};
`;

/**
 * Scroll container for the step list. The {@link LogCard} sets
 * {@code overflow: hidden} so this inner region carries the scroll
 * behaviour, which keeps the run header / metadata grid pinned at the
 * top of the card while a long step list scrolls beneath.
 */
export const StepsScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;
